import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import "./myPosts.css";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

interface Post {
  id: number;
  caption: string;
  media_url: string;
  media_type: string;
  created_at: string;
  likes: number;
  comments_count: number;
}

interface Profile {
  name: string;
  avatar: string;
}

export default function MyPostsPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activePost, setActivePost] = useState<Post | null>(null);
  const [editCaption, setEditCaption] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchMyPosts();
  }, []);

  const fetchMyPosts = async () => {
    try {
      setLoading(true);

      const [postsRes, profileRes] = await Promise.all([
        axios.get(`${API}/posts/me`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setPosts(postsRes.data || []);
      setProfile({
        name: profileRes.data?.name || "User",
        avatar: profileRes.data?.avatar || "",
      });
    } catch (e: any) {
      setError("Failed to load your posts");
    } finally {
      setLoading(false);
    }
  };

  const changeAvatar = async (file: File) => {
    try {
      const form = new FormData();
      form.append("avatar", file);

      await axios.put(`${API}/profile/me`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const profileRes = await axios.get(`${API}/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProfile({
        name: profileRes.data?.name || "User",
        avatar: profileRes.data?.avatar || "",
      });
    } catch (e) {
      alert("Failed to update profile picture");
    }
  };

  const openPost = (post: Post) => {
    setActivePost(post);
    setEditCaption(post.caption);
  };

  const closeModal = () => {
    setActivePost(null);
    setEditCaption("");
  };

  const saveEdit = async () => {
    if (!activePost) return;

    try {
      setSaving(true);

      await axios.put(
        `${API}/posts/${activePost.id}`,
        { caption: editCaption },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPosts((prev) =>
        prev.map((p) =>
          p.id === activePost.id ? { ...p, caption: editCaption } : p
        )
      );

      setActivePost((prev) =>
        prev ? { ...prev, caption: editCaption } : prev
      );
    } catch (e) {
      alert("Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async () => {
    if (!activePost) return;

    const confirmDelete = window.confirm("Are you sure you want to delete this post?");
    if (!confirmDelete) return;

    try {
      setDeleting(true);

      await axios.delete(`${API}/posts/${activePost.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPosts((prev) => prev.filter((p) => p.id !== activePost.id));
      closeModal();
    } catch (e) {
      alert("Failed to delete post");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <motion.div
      className="myposts-root"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <div className="myposts-glow" />

      <div className="myposts-shell">
        {/* PROFILE HEADER */}
        <motion.div
          className="myposts-profile-card"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div
            className="profile-avatar-wrap clickable"
            onClick={() => fileInputRef.current?.click()}
          >
            <img
              src={
                profile?.avatar ||
                "https://ui-avatars.com/api/?background=FFD600&color=000&size=256"
              }
              alt="avatar"
            />
            <div className="avatar-overlay">Change</div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) changeAvatar(file);
              }}
            />
          </div>

          <div className="profile-meta">
            <h2>{profile?.name || "Your Profile"}</h2>
            <span>{posts.length} Posts</span>
          </div>
        </motion.div>

        <motion.h1
          className="myposts-title"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          My Posts
        </motion.h1>

        {loading && <div className="myposts-loading">Loading…</div>}
        {error && <div className="myposts-error">{error}</div>}

        <div className="myposts-grid">
          <AnimatePresence>
            {posts.map((post, idx) => (
              <motion.div
                key={post.id}
                className="mypost-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ rotateX: 6, rotateY: -6, scale: 1.03 }}
                onClick={() => openPost(post)}
              >
                <div className="mypost-media">
                  {post.media_type === "video" ? (
                    <video src={post.media_url} muted loop />
                  ) : (
                    <img src={post.media_url} alt="post" />
                  )}
                </div>

                <div className="mypost-content">
                  <p className="mypost-caption">{post.caption}</p>

                  <div className="mypost-meta">
                    <span>❤️ {post.likes}</span>
                    <span>💬 {post.comments_count}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      <AnimatePresence>
        {activePost && (
          <motion.div
            className="post-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="post-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-media">
                {activePost.media_type === "video" ? (
                  <video src={activePost.media_url} controls />
                ) : (
                  <img src={activePost.media_url} alt="full" />
                )}
              </div>

              <div className="modal-side">
                <h3>Edit Caption</h3>
                <textarea
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                />

                <div className="modal-actions">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    className="save-btn"
                    onClick={saveEdit}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save"}
                  </motion.button>

                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    className="delete-btn"
                    onClick={deletePost}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting…" : "Delete"}
                  </motion.button>

                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    className="close-btn"
                    onClick={closeModal}
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
